import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, Newspaper } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { API } from "@/lib/api";

const inputCls =
  "bg-midnight border-[#1E293B] text-[#F4F6F8] placeholder:text-[#94A3B8]/50 focus-visible:ring-gold/60 h-12 rounded-sm";

const initialForm = { title: "", category: "", excerpt: "", body: "", meta_title: "", meta_description: "", publish_at: "" };

export const InsightsManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/insights", { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("cover", file);
      await api.post("/admin/insights", fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Insight published — live at /insights");
      setForm(initialForm);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to publish insight");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/insights/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(items.filter((i) => i.id !== id));
      toast.success("Insight removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="insights-manager">
      <form onSubmit={add} data-testid="admin-insight-form" className="bg-surface border border-[#1E293B] p-6 space-y-4 h-fit">
        <h3 className="font-display font-semibold text-[#F4F6F8]">Publish a trade insight</h3>
        <p className="text-xs text-[#94A3B8]">Market updates help buyers find you through search. Live immediately at /insights.</p>
        <div>
          <Label htmlFor="in-title" className="text-xs uppercase tracking-wider text-[#94A3B8]">Title *</Label>
          <Input id="in-title" data-testid="admin-insight-title" required value={form.title} onChange={set("title")}
            className={inputCls} placeholder="e.g. West African sesame demand rises ahead of Q4" />
        </div>
        <div>
          <Label htmlFor="in-category" className="text-xs uppercase tracking-wider text-[#94A3B8]">Category *</Label>
          <Input id="in-category" data-testid="admin-insight-category" required value={form.category} onChange={set("category")}
            className={inputCls} placeholder="e.g. Agriculture, Energy, Logistics" />
        </div>
        <div>
          <Label htmlFor="in-excerpt" className="text-xs uppercase tracking-wider text-[#94A3B8]">Excerpt *</Label>
          <Textarea id="in-excerpt" data-testid="admin-insight-excerpt" required rows={2} value={form.excerpt} onChange={set("excerpt")}
            className={`${inputCls} h-auto`} placeholder="One or two sentences shown on the card..." />
        </div>
        <div>
          <Label htmlFor="in-body" className="text-xs uppercase tracking-wider text-[#94A3B8]">Article *</Label>
          <Textarea id="in-body" data-testid="admin-insight-body" required rows={7} value={form.body} onChange={set("body")}
            className={`${inputCls} h-auto`} placeholder="Full article text. Separate paragraphs with a blank line." />
        </div>
        <div className="border border-[#1E293B] p-4 space-y-4">
          <p className="text-xs uppercase tracking-wider text-gold">SEO (optional)</p>
          <div>
            <Label htmlFor="in-meta-title" className="text-xs uppercase tracking-wider text-[#94A3B8]">Meta Title</Label>
            <Input id="in-meta-title" data-testid="admin-insight-meta-title" value={form.meta_title} onChange={set("meta_title")}
              className={inputCls} placeholder="Defaults to the article title" />
          </div>
          <div>
            <Label htmlFor="in-meta-desc" className="text-xs uppercase tracking-wider text-[#94A3B8]">Meta Description</Label>
            <Textarea id="in-meta-desc" data-testid="admin-insight-meta-description" rows={2} value={form.meta_description} onChange={set("meta_description")}
              className={`${inputCls} h-auto`} placeholder="Defaults to the excerpt — 150–160 characters works best" />
          </div>
        </div>
        <div>
          <Label htmlFor="in-publish" className="text-xs uppercase tracking-wider text-[#94A3B8]">Schedule publication (optional)</Label>
          <Input id="in-publish" data-testid="admin-insight-publish-at" type="datetime-local" value={form.publish_at} onChange={set("publish_at")} className={inputCls} />
          <p className="text-[11px] text-[#94A3B8] mt-1">Leave empty to publish immediately. Scheduled articles go live automatically at the chosen time.</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-[#94A3B8]">Cover image (optional)</Label>
          <input ref={fileRef} id="in-cover" type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
            data-testid="admin-insight-cover-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <label htmlFor="in-cover" data-testid="admin-insight-cover-label"
            className="mt-1 flex items-center gap-3 border border-dashed border-[#1E293B] hover:border-gold/50 transition-colors duration-200 px-4 py-3.5 cursor-pointer text-sm text-[#94A3B8]">
            <Upload className="w-4 h-4 text-gold shrink-0" />
            {file ? <span className="text-[#F4F6F8]">{file.name}</span> : "Upload cover image"}
          </label>
        </div>
        <button data-testid="admin-insight-publish" type="submit" disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-gold text-midnight font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors duration-200 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Publish Insight
        </button>
      </form>

      <div className="space-y-3" data-testid="admin-insights-list">
        {items.length === 0 && (
          <div className="border border-dashed border-[#1E293B] p-10 flex flex-col items-center gap-3 text-sm text-[#94A3B8]" data-testid="admin-insights-empty">
            <Newspaper className="w-6 h-6" /> No insights published yet.
          </div>
        )}
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-insight-${p.id}`} className="bg-surface border border-[#1E293B] p-5 flex gap-4">
            {p.cover && (
              <img src={`${API}/public/files/${p.cover.storage_path}`} alt="" className="w-16 h-16 object-cover border border-[#1E293B] shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{p.category}</p>
              {p.publish_at && new Date(p.publish_at) > new Date() && (
                <span data-testid={`admin-insight-scheduled-${p.id}`} className="inline-block mt-1 text-[10px] uppercase tracking-wider text-amber-300 border border-amber-300/40 px-2 py-0.5 rounded-full">
                  Scheduled · {new Date(p.publish_at).toLocaleString()}
                </span>
              )}
              <p className="text-sm font-semibold text-[#F4F6F8] mt-1">{p.title}</p>
              <p className="text-xs text-[#94A3B8] mt-1 line-clamp-2">{p.excerpt}</p>
            </div>
            <button data-testid={`admin-insight-delete-${p.id}`} onClick={() => del(p.id)}
              className="text-[#94A3B8] hover:text-red-400 transition-colors duration-200 shrink-0" aria-label="Delete insight">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
